import { database } from '@app/firebase';
import AppBackgroundColor from '@components/app-background-color/AppBackgroundColor';
import ButtonLink from '@components/button-link/ButtonLink';
import HomesCarousel from '@components/carousels/HomesCarousel';
import HeroAlt from '@components/hero-alt/HeroAlt';
import PressLogos from '@components/press-logos/PressLogos';
import Testimonials from '@components/testimonials/Testimonials';
import { ContentBlock } from '@layouts/ContentBlocks';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import useBreakpoints from '@ui/hooks/useBreakpoints';
import makeStoreUrl from '@ui/utils/makeStoreUrl';
import { collection, getDocs, limit, orderBy, query, where } from 'firebase/firestore';
import { GetServerSideProps } from 'next';
import Head from 'next/head';
import Image from 'next/image';
import { LocalBusinessJsonLd } from 'next-seo';
import React from 'react';

import SocialProofBrands from '../components/blocks/SocialProofBrands';
import useStyles from './Homepage.style';

const jslonld = `{
  "@context": "https://schema.org/",
  "@type": "WebSite",
  "name": "Guest House",
  "url": "https://guesthouseshop.com/",
  "potentialAction": {
    "@type": "SearchAction",
    "target": "https://guesthouseshop.com/shop?query={search_term_string}",
    "query-input": "required name=search_term_string"
  }
}`;

const WebSiteJsonLd = () => {
  return (
    <Head>
      <script
        key="jsonld-website"
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: jslonld }}
      />
    </Head>
  );
};

export const getServerSideProps: GetServerSideProps = async () => {
  const homes = await getDocs(query(collection(database, 'homes')
    ,where('published', '==', true)
    ,where('featured', '==', true)
    ,orderBy('install_date', 'desc')
    ,limit(10)));

  const data = {
    homes: JSON.stringify(homes.docs.map(h => h.data())),
  };

  return { props: { data } };
};

function Homepage(props) {
  const { theme } = useStyles();
  const { md } = useBreakpoints();
  const { classes } = useStyles();

  const homes = JSON.parse(props.data.homes) as guesthouse.Home[];

  return (
    <>
      <AppBackgroundColor
        backgroundColor="#FFFFFF"
        borderColor="#FFFFFF"
        headerColor="#FFFFFF"
      />

      {WebSiteJsonLd()}

      <LocalBusinessJsonLd
        type="LocalBusiness"
        id=""
        name="Guest House"
        description=""
        url={makeStoreUrl()}
        telephone="858-254-2752"
        address={{
          streetAddress: '10040 W 27th Ave',
          addressLocality: 'Wheat Ridge',
          addressRegion: 'CO',
          postalCode: '80215',
          addressCountry: 'US',
        }}
        geo={{
          latitude: '39.75518599999999',
          longitude: '-105.1108284',
        }}
        images={[
          'https://firebasestorage.googleapis.com/v0/b/guesthouse-cms-dev.appspot.com/o/public%2Fguesthouse-social-default.jpg?alt=media',
        ]}
        sameAs={[
          'https://facebook.com/guesthouseshop',
          'https://instagram.com/guesthouseshop',
          'https://pinterest.com/guesthouseshop',
        ]}
      />

      <ContentBlock margin={md ? [0, 0, 0, 0] : 0}>
        <HeroAlt
          title={
            <>
              Get your next listing 
              {' '}
              <br />
              market-ready in days
            </>
          }
          subText={
            <>
              List for sale 10x faster with Guest House — premium staging,
              <br style={{ display: md ? 'block' : 'none' }} />
              cleaning and photography, all in one place. Goodbye, vendor chaos.
            </>
          }
          backgroundImage="/images/homepage/header.png"
          ctaButtons={[
            {
              label: 'Get an instant quote',
              href: '/home-staging/get-a-quote',
              variant: 'contained',
              color: 'primary',
            },
            {
              label: 'Speak with our team',
              href: 'https://guesthouseshop-8563089.hs-sites-na2.com/book-now',
              variant: 'outlined',
              color: 'secondary',
              target: '_blank',
            },
          ]}
        />
      </ContentBlock>

      <SocialProofBrands />
     
      <ContentBlock
        maxWidth={1308}
        margin={md ? [7.5, 0, 10, 0] : [4, 0]}

      >
        <Box
          display={md ? 'flex' : 'block'}
          alignItems="center"
          justifyContent="center"
          gap={2}
        >
          <Box
            flex={1}
            sx={{
              marginBottom: md ? 0 : '48px',
              'a': {
                textDecoration: 'none !important',
              }
            }}
          >
            <Typography
              fontFamily={theme.gh_vars.circular}
              variant="h2"
              style={{ fontSize: md ? 48 : 24, letterSpacing: '-2%', maxWidth: 450, marginBottom: '24px' }}
            >
              Staging that moves as fast as you do
            </Typography>
            <Typography
              fontFamily={theme.gh_vars.circular}
              variant="body1"
              style={{ fontSize: md ? 20 : 18, marginBottom: '48px' }}
            >
              Guest House helps you prep homes for sale in days — not weeks. Book all of your services in minutes and work with our local team. We handle every detail, so you can list faster and sell for more.
            </Typography>
            <Button
              variant="contained"
              color="white"
              component={ButtonLink}
              href="/home-staging"
              style={{
                display: 'block',
                fontWeight: 'bold',
                backgroundColor: '#080808',
                color: '#ffffff',
                textDecoration: 'none !important',
                borderRadius: '30px',
                padding: '16px 24px',
                lineHeight: 1.4,
                height: 'auto',
                marginRight: '16px',
                fontSize: md ? 20 : 16,
              }}
            >
              Learn more
            </Button>
          </Box>
          <Box
            display="grid"
            gridTemplateColumns="repeat(2, 1fr)"
            gap={'24px'}
            alignItems="center"
            justifyContent="start"
            flex={1}
          >
            <Box sx={{ position: 'relative', height: '198px', borderRadius: '20px' }}>
              <Image
                fill
                src="/images/homepage/as-fast-2.png"
                alt="Staging 1"
                style={{ objectFit: 'cover', borderRadius: '20px' }}
              />
            </Box>
            <Box sx={{ height: '198px', backgroundColor: '#EEECEA', borderRadius: '20px', position: 'relative' }}>
              <Image
                fill
                src="/images/homepage/backed-by-naor.png"
                alt="Staging 1"
                style={{ objectFit: 'contain', padding: md ? '24px' : '16px' }}
              />
            </Box>
            <Box sx={{ height: '198px', backgroundColor: '#CCD5D6', borderRadius: '20px', position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column' }}>
              <Image
                src="/images/homepage/homes-staged.png"
                alt="Staging 1"
                width={36}
                height={36}
                style={{ marginBottom: '12px' }}
              />
              <Typography
                variant="body1"
                style={{ fontSize: 18, textAlign: 'center' }}
              >
                2,000+ homes staged
              </Typography>
            </Box>
            <Box sx={{ position: 'relative', height: '198px', borderRadius: '20px' }}>
              <Image
                fill
                src="/images/homepage/as-fast-3.png"
                alt="Staging 1"
                style={{ objectFit: 'cover', borderRadius: '20px' }}
              />
            </Box>
          </Box>
        </Box>
      </ContentBlock>

      <ContentBlock
        padding={md ? [15, 0, 15, 0] : [4, 0]}
        style={{ backgroundColor: '#E9E6E3' }}
        margin={[0]}
        maxWidth={'none'}
      >
        <ContentBlock
          maxWidth={1308}
          margin={[0, 'auto']}
        >
          <Box
            display={md ? 'flex' : 'block'}
            alignItems="flex-start"
            justifyContent="center"
            gap={10}
          >
            <Box
              flex={1}
            >
              <Typography
                fontFamily={theme.gh_vars.circular}
                variant="h2"
                style={{ fontSize: md ? 48 : 24, letterSpacing: '-2%', marginBottom: md ? '48px' : '24px' }}
              >
                Maximize curb appeal without the hassle
              </Typography>
              <Box
                sx={{ position: 'relative', marginBottom: md ? 0 : '24px', height: md ? '500px' : '300px', borderRadius: '20px', 'a': {
                  textDecoration: 'none !important',
                } }}
              >
                <Image
                  fill
                  src="/images/homepage/consultation.png"
                  alt=""
                  style={{ objectFit: 'cover', borderRadius: '20px', }}
                />
                <Button
                  variant="contained"
                  color="white"
                  component={ButtonLink}
                  href="/home-staging/get-a-quote"
                  style={{
                    position: 'absolute',
                    top: '50%',
                    transform: 'translate(-50%, -50%)',
                    left: '50%',
                    display: 'block',
                    fontWeight: '400',
                    backgroundColor: '#FFFFFF',
                    color: '#080808',
                    textDecoration: 'none !important',
                    borderRadius: '20px',
                    padding: '16px 24px',
                    lineHeight: 1.4,
                    height: 'auto',
                    fontSize: 20,
                    textAlign: 'center',
                    width: md ? 'auto' : '280px',
                  }}
                >
                  Get an instant quote
                </Button>
              </Box>
            </Box>
            <Box flex={1}>
              <Typography
                fontFamily={theme.gh_vars.circular}
                variant="body1"
                style={{ fontSize: md ? 20 : 18, marginBottom: md ? '54px' : '24px' }}
              >
                We bring together premium design, trusted professionals and smooth project management to guarantee your listing stands out every time.
              </Typography>
              <Typography
                fontFamily={theme.gh_vars.circular}
                variant="body1"
                style={{ fontSize: md ? 18 : 18, marginBottom: '24px', fontWeight: 600 }}
              >
                We&apos;ve got you covered, whether you&apos;re a:
              </Typography>
              <Box className={classes.customerTypeItem}>
                <Image
                  src="/images/homepage/chart-circle-icon.png"
                  alt="Top Agent"
                  width={md ? 32 : 24}
                  height={md ? 32 : 24}
                  style={{ marginRight: '16px' }}
                />
                <Typography
                  variant="body1"
                  style={{ fontSize: md ? 20 : 16 }}
                >
                  <b>
                    Top Agent
                  </b>
                  {' '}
                  needing standout marketing
                </Typography>
              </Box>
              <Box className={classes.customerTypeItem}>
                <Image
                  src="/images/homepage/agency-icon.png"
                  alt="Top Agent"
                  width={md ? 32 : 24}
                  height={md ? 32 : 24}
                  style={{ marginRight: '16px' }}
                />                
                {' '}
                <Typography
                  variant="body1"
                  style={{ fontSize: md ? 20 : 16 }}
                >
                  <b>
                    Agency Team
                  </b>
                  {' '}
                  looking for consistent, scalable support
                </Typography>
              </Box>
              <Box className={classes.customerTypeItem}>
                <Image
                  src="/images/homepage/flipper-icon.png"
                  alt="Top Agent"
                  width={md ? 32 : 24}
                  height={md ? 32 : 24}
                  style={{ marginRight: '16px' }}
                />
                <Typography
                  variant="body1"
                  style={{ fontSize: md ? 20 : 16 }}
                >
                  <b>
                    Flipper or Builder
                  </b>
                  {' '}
                  juggling multiple projects
                </Typography>
              </Box>
              <Box className={classes.customerTypeItem}>
                <Image
                  src="/images/homepage/seller-icon.png"
                  alt="Top Agent"
                  width={md ? 32 : 24}
                  height={md ? 32 : 24}
                  style={{ marginRight: '16px' }}
                />
                <Typography
                  variant="body1"
                  style={{ fontSize: md ? 20 : 16 }}
                >
                  <b>
                    Homeowner
                  </b>
                  {' '}
                  who wants a higher sale price, faster.
                </Typography>
              </Box>
            </Box>
              
          </Box>
          
        </ContentBlock>
      </ContentBlock>

      <ContentBlock
        margin={[0]}
        padding={[4, 0, 4, 0]}
        paddingMd={[15, 0, 15, 0]}
        style={{
          backgroundColor: '#CCD5D6'
        }}
        maxWidth={'none'}
      >
        <ContentBlock
          margin={[0, 'auto']}
          
          maxWidth={1308}
        >
          <Typography
            variant="body1"
            component="h2"
            style={{ fontSize: md ? 20 : 16,marginBottom: 8 }}
          >
            Reviews
          </Typography>
          <Typography
            variant="h4Alt"
            style={{ fontSize: md ? 48 : 28, marginBottom: md ? theme.spacing(5) : theme.spacing(3) }}
          >
            What customers are saying
          </Typography>
         
          <Testimonials backgroundColor="#ffffff" />
         
        </ContentBlock>
      </ContentBlock>

      <ContentBlock
        maxWidth={'none'}
        style={{ backgroundColor: '#f6f6f7', overflowX: 'hidden' }}
        padding={[4, 0, 6, 0]}
        paddingMd={[12, 0, 12, 0]}
        margin={0}
      >
        <ContentBlock
          maxWidth={1308}
          margin={[0, 'auto']}
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              '& a': {
                textDecoration: 'none !important'
              }
            }}
          >
            <Box>
              <Typography
                variant="h4Alt"
                style={{ fontSize: md ? 48 : 28, marginBottom: 8 }}
              >
                Recent listings
                {' '}
              </Typography>
         
              <Typography
                variant="body1"
                component="h2"
                style={{ fontSize: md ? 20 : 16, marginBottom: 60 }}
              >
                Check out our work in Colorado, California, Arizona & beyond
                {' '}
              </Typography>
            </Box>
            <Button
              variant="contained"
              color="white"
              component={ButtonLink}
              href="/shop/homes"
              style={{
                display: 'block',
                fontWeight: 'bold',
                backgroundColor: '#080808',
                color: '#ffffff',
                textDecoration: 'none !important',
                borderRadius: '30px',
                padding: md ? '16px 24px' : '12px 16px',
                lineHeight: 1.4,
                height: 'auto',
                marginRight: '16px',
                fontSize: md ? '20px' : '14px',
                border: 0,
              }}
            >
              See all listings
            </Button>
          </Box>
         

          {!!homes?.length && (
            <HomesCarousel
              seeAll
              homes={homes}
            />
          )}
        </ContentBlock>
      </ContentBlock>
 

      <ContentBlock
        margin={[0]}
        width={'100vw'}
      >
        <div
          style={{     
            width: '100vw',
            position: 'relative',
            left: '50%',
            transform: 'translateX(-50%)',
            maxWidth: 'none', 
            height: md ? 561 : 434, 
            overflow: 'hidden',  
            borderRadius: 0, 
            marginTop: 0, 
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <div
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              background: 'rgba(0, 0, 0, 0.2)',
              zIndex: 2,
            }}
          >

          </div>
          <Image
            fill
            src="/images/homepage/custom-design.png"
            alt=""
            sizes="100vw"
            style={{
              objectFit: 'cover',
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              zIndex: 1,
            }}
          />
          <div className={classes.subHeroTextContainer}>
            <Typography
              fontFamily={theme.gh_vars.circular}
              variant="h2"
              component="h3"
              style={{ marginBottom: md ? theme.spacing(3) : theme.spacing(1), fontSize: md ? 48 : 24 }}
            >
              Make the move easy-peasy
            </Typography>
            <Typography
              variant="body1"
              style={{ fontSize: md ? 20 : 15, lineHeight: 1.5, maxWidth: md ? 600 : 350, marginBottom: md ? theme.spacing(6) : theme.spacing(4) }}
            >
              Need help moving out and getting organized? Book everything from packing to decluttering to cleaning. Then sit back and relax.
            </Typography>

            <Button
              variant="contained"
              color="white"
              component={ButtonLink}
              href="/home-staging/get-a-quote"
              style={{
                display: 'inline-block',
                fontWeight: 'bold',
                backgroundColor: '#ffffff',
                color: '#080808',
                textDecoration: 'none !important',
                borderRadius: '30px',
                padding: '16px 24px',
                lineHeight: 1.4,
                height: 'auto',
                marginRight: '16px',
                border: 'none',
                fontSize: md ? 18 : 16,
              }}
            >
              Get an instant quote
            </Button>
        
          </div>
        </div>
      </ContentBlock>

      <ContentBlock
        margin={[0]}
        padding={[4, 0, 4, 0]}
        paddingMd={[8, 0, 15, 0]}
        style={{ backgroundColor: '#DDDDDD' }}
        maxWidth={'none'}
      >
        <ContentBlock
          margin={[0, 'auto']}
          maxWidth={1308}
        >
          <Box style={{ width: '100%', textAlign: 'center' }}>
            <Typography
              gutterBottom
              variant="h2"
              style={{ marginBottom: md ? theme.spacing(11) : theme.spacing(5), fontSize: md ? 48 : 28, color: '#737373', letterSpacing: '-2%' }}
            >
              Featured in
            </Typography>
          </Box>

          <PressLogos />
        </ContentBlock>
      </ContentBlock>

      <ContentBlock
        maxWidth={1308}
        margin={[0, 'auto']}
        padding={[4, 3, 4, 3]}
        paddingMd={[15, 0, 15, 0]}
      >
        <Box
          display="flex"
          flexDirection={md ? 'row' : 'column'}
          gap={6}
          sx={{
            '& a': {
              textDecoration: 'none !important'
            }
          }}
        >
          <Box flex={1}>
            <Typography
              variant="h3Alt"
              component="h2"
              className={classes.infoHeader}
              style={{ fontSize: md ? 48 : 24, letterSpacing: '-2%' }}
            >
              Quickly book photos, virtual tours & more
            </Typography>
            <Typography
              variant="body1"
              style={{ fontSize: md ? 20 : 15, lineHeight: 1.5, marginBottom: md ? theme.spacing(6) : theme.spacing(4) }}
            >
              Guest House covers every detail of listing prep so you don&apos;t have to, including MLS photography, video and 3D tours. Stop juggling multiple vendors and start saving time today.
            </Typography>
            <Button
              variant="contained"
              color="white"
              component={ButtonLink}
              href="/home-staging/get-a-quote"
              style={{
                display: 'block',
                fontWeight: 'bold',
                backgroundColor: '#080808',
                color: '#ffffff',
                textDecoration: 'none !important',
                borderRadius: '30px',
                padding: '16px 24px',
                lineHeight: 1.4,
                height: 'auto',
                marginRight: '16px',
                border: 'none',
                fontSize: md ? 18 : 16,
              }}
            >
              Get an instant quote
            </Button>
          </Box>
          <Box flex={1}>
          
            <Box sx={{ position: 'relative', height: '395px', borderRadius: '20px' }}>
              <Image
                fill
                src={'/images/homepage/instantly-book.jpg'}
                alt=""
                sizes="100vw"
                style={{
                  objectFit: 'cover',
                  borderRadius: '20px'
                }}
              />
            </Box>
           
          </Box>
          
        </Box>
      </ContentBlock>

 
    </>
  );
}

export default Homepage;
